import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ArrowLeftRight, CalendarCheck, Info, Layers, Receipt } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { MoneyInput } from '@components/MoneyInput';
import transactionService from '@services/transactionService';
import { DEFAULT_SCHEDULE_LEAD_DAYS, type Account, type Category, type Transaction } from '@/types/finance';
import { formatDateBR, todayISO } from '@/utils/formatters';
import { previewInstallments } from '@/utils/installments';
import { formatBRL } from '@/utils/money';
import { CategorySelect, MovementToggle, PillTabs, Required, ScheduleFields, labelCls } from './financeBits';

type Mode = 'single' | 'installments' | 'transfer';

interface Props {
  /** Edição (sem abas: só o lançamento). */
  transaction?: Transaction | null;
  /** Contas ativas para os seletores. */
  accounts: Account[];
  categories: Category[];
  /** Conta pré-selecionada (atalho do extrato). */
  prefillAccountId?: number;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}

const MIN_INSTALLMENTS = 2;
const MAX_INSTALLMENTS = 72;

const signed = (movement: 'expense' | 'income', abs: number) => (movement === 'expense' ? -Math.abs(abs) : Math.abs(abs));

const AccountOptions: React.FC<{ accounts: Account[]; current?: Transaction | null }> = ({ accounts, current }) => {
  // Em edição a conta pode estar inativa (não vem na lista): mantém como opção.
  const list = current && !accounts.some((a) => a.id === current.account_id)
    ? [...accounts, { id: current.account_id, name: `${current.account_name} (inativa)` } as Account]
    : accounts;
  return (
    <>
      <option value="">Selecione…</option>
      {list.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </>
  );
};

/**
 * Novo lançamento com três abas — Lançamento (POST /transactions), Parcelado
 * (POST /transactions/installments, prévia calculada no front com as mesmas regras do backend) e
 * Transferência (POST /transactions/transfer) — e edição de um lançamento (PUT). O usuário escolhe
 * Despesa/Receita e digita o valor absoluto; o sinal é aplicado aqui. Fase 7: despesa avulsa pode
 * "Precisa agendar no banco" (+ dias de aviso); em linha de recorrência isso vem da regra.
 */
export const TransactionFormModal: React.FC<Props> = ({ transaction, accounts, categories, prefillAccountId, onClose, onSaved, onError }) => {
  const isEdit = !!transaction;
  const isTransferLeg = !!transaction?.transfer_id;
  const isInstallment = !!transaction?.transaction_group && !!transaction?.repeat_total;
  const [mode, setMode] = useState<Mode>('single');
  const [saving, setSaving] = useState(false);

  const defaultAccount = prefillAccountId ?? (accounts.length === 1 ? accounts[0].id : '');

  // ---- Lançamento simples / edição
  const [movement, setMovement] = useState<'expense' | 'income'>(() => (transaction && transaction.amount_cents > 0 ? 'income' : 'expense'));
  const [accountId, setAccountId] = useState<number | ''>(transaction?.account_id ?? defaultAccount);
  const [date, setDate] = useState(transaction?.transaction_date ?? todayISO());
  const [description, setDescription] = useState(transaction?.description ?? '');
  const [amount, setAmount] = useState<number | null>(transaction ? Math.abs(transaction.amount_cents) : null);
  const [categoryId, setCategoryId] = useState<number | ''>(transaction?.category_id ?? '');
  const [notes, setNotes] = useState(transaction?.notes ?? '');
  const [requiresScheduling, setRequiresScheduling] = useState(transaction?.requires_scheduling ?? false);
  const [leadDays, setLeadDays] = useState(transaction?.schedule_lead_days ?? DEFAULT_SCHEDULE_LEAD_DAYS);
  /** Linha gerada por recorrência: agendamento vem da regra (o backend ignora o do corpo). */
  const fromRecurring = !!transaction?.recurring_transaction_id;
  /** Agendamento no banco: só despesa comum (não transferência nem espelho de fatura). */
  const schedulingAllowed = movement === 'expense' && !isTransferLeg && !transaction?.credit_card_invoice_id;
  /** Campos de agendamento do corpo: só em despesa avulsa (receita → 400; recorrência → ignorado). */
  const scheduleFields = () =>
    schedulingAllowed && !fromRecurring ? { requires_scheduling: requiresScheduling, schedule_lead_days: leadDays } : {};

  // ---- Parcelado
  const [instMovement, setInstMovement] = useState<'expense' | 'income'>('expense');
  const [instAccountId, setInstAccountId] = useState<number | ''>(defaultAccount);
  const [firstDate, setFirstDate] = useState(todayISO());
  const [instDescription, setInstDescription] = useState('');
  const [total, setTotal] = useState<number | null>(null);
  const [count, setCount] = useState(2);
  const [instCategoryId, setInstCategoryId] = useState<number | ''>('');
  const [instNotes, setInstNotes] = useState('');

  // ---- Transferência
  const [fromId, setFromId] = useState<number | ''>(defaultAccount);
  const [toId, setToId] = useState<number | ''>('');
  const [trDate, setTrDate] = useState(todayISO());
  const [trAmount, setTrAmount] = useState<number | null>(null);
  const [trDescription, setTrDescription] = useState('');
  const [trNotes, setTrNotes] = useState('');

  const preview = useMemo(
    () => (total && count >= MIN_INSTALLMENTS && firstDate ? previewInstallments(signed(instMovement, total), count, firstDate) : []),
    [total, count, firstDate, instMovement],
  );

  const canSingle = !saving && accountId !== '' && !!date && !!description.trim() && !!amount;
  const canInstallments =
    !saving && instAccountId !== '' && !!firstDate && !!instDescription.trim() && !!total && count >= MIN_INSTALLMENTS && count <= MAX_INSTALLMENTS;
  const canTransfer = !saving && fromId !== '' && toId !== '' && fromId !== toId && !!trDate && !!trAmount;

  const canSubmit = isEdit || mode === 'single' ? canSingle : mode === 'installments' ? canInstallments : canTransfer;

  const save = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      if (isEdit && transaction) {
        await transactionService.update(transaction.id, {
          account_id: Number(accountId),
          transaction_date: date,
          description: description.trim(),
          amount_cents: signed(movement, amount as number),
          category_id: isTransferLeg ? transaction.category_id ?? null : categoryId === '' ? null : categoryId,
          notes: notes.trim() || null,
          ...scheduleFields(),
        });
      } else if (mode === 'single') {
        await transactionService.create({
          account_id: Number(accountId),
          transaction_date: date,
          description: description.trim(),
          amount_cents: signed(movement, amount as number),
          category_id: categoryId === '' ? null : categoryId,
          notes: notes.trim() || null,
          ...scheduleFields(),
        });
      } else if (mode === 'installments') {
        await transactionService.createInstallments({
          account_id: Number(instAccountId),
          first_date: firstDate,
          description: instDescription.trim(),
          total_cents: signed(instMovement, total as number),
          installments: count,
          category_id: instCategoryId === '' ? null : instCategoryId,
          notes: instNotes.trim() || null,
        });
      } else {
        await transactionService.createTransfer({
          from_account_id: Number(fromId),
          to_account_id: Number(toId),
          transaction_date: trDate,
          amount_cents: Math.abs(trAmount as number),
          description: trDescription.trim() || null,
          notes: trNotes.trim() || null,
        });
      }
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? (isTransferLeg ? 'Editar transferência' : 'Editar lançamento') : 'Novo lançamento';

  const singleForm = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {isTransferLeg && transaction && (
        <div className="sm:col-span-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 flex gap-2">
          <Info size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            Esta é uma perna da transferência com <strong>{transaction.transfer_account_name ?? 'a outra conta'}</strong>. Data, valor,
            descrição e observações são sincronizados na outra perna; a categoria é a de sistema e não pode ser trocada. Trocar a
            conta muda só esta perna.
          </span>
        </div>
      )}
      {isInstallment && transaction && (
        <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex gap-2">
          <Layers size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            Parcela <strong>{transaction.repeat_index}/{transaction.repeat_total}</strong>. A edição vale só para esta parcela.
          </span>
        </div>
      )}
      <div className="sm:col-span-2">
        <MovementToggle value={movement} onChange={setMovement} disabled={isTransferLeg} />
      </div>
      <div>
        <label className={labelCls}>
          Conta
          <Required />
        </label>
        <select className="select-field" value={accountId} onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : '')}>
          <AccountOptions accounts={accounts} current={transaction} />
        </select>
      </div>
      <div>
        <label className={labelCls}>
          Data
          <Required />
        </label>
        <DateInputBR className="input-field w-full" value={date} onChange={setDate} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>
          Descrição
          <Required />
        </label>
        <input className="input-field" maxLength={200} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex.: Supermercado" />
      </div>
      <div>
        <label className={labelCls}>
          Valor
          <Required />
        </label>
        <MoneyInput value={amount} onChange={setAmount} />
      </div>
      <div>
        <label className={labelCls}>Categoria</label>
        {isTransferLeg ? (
          <input className="input-field" value={transaction?.category_name ?? 'Transferência entre contas'} disabled />
        ) : (
          <CategorySelect categories={categories} movement={movement} value={categoryId} onChange={setCategoryId} />
        )}
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>Observações</label>
        <textarea className="textarea-field" rows={2} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {schedulingAllowed &&
        (fromRecurring ? (
          <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex gap-2">
            <CalendarCheck size={16} className="flex-shrink-0 mt-0.5 text-slate-500" />
            <span>
              {transaction?.requires_scheduling ? (
                <>
                  Pago por agendamento no banco (avisa {transaction.schedule_lead_days ?? DEFAULT_SCHEDULE_LEAD_DAYS} dias antes): vem da recorrência — mude por Finanças → Recorrências.
                </>
              ) : (
                <>O agendamento no banco de lançamentos recorrentes vem da recorrência — marque "Pago por agendamento no banco" em Finanças → Recorrências.</>
              )}{' '}
              Marcar como agendada pela lista não desvincula da recorrência.
            </span>
          </div>
        ) : (
          <div className="sm:col-span-2">
            <ScheduleFields
              checked={requiresScheduling}
              onCheckedChange={setRequiresScheduling}
              leadDays={leadDays}
              onLeadDaysChange={setLeadDays}
              label="Precisa agendar no banco"
              hint="Avisa no painel Hoje, no sino e por e-mail enquanto não for marcado como agendado (botão na lista de lançamentos)."
            />
          </div>
        ))}
    </div>
  );

  const installmentsForm = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2">
        <MovementToggle value={instMovement} onChange={setInstMovement} />
      </div>
      <div>
        <label className={labelCls}>
          Conta
          <Required />
        </label>
        <select className="select-field" value={instAccountId} onChange={(e) => setInstAccountId(e.target.value ? Number(e.target.value) : '')}>
          <AccountOptions accounts={accounts} />
        </select>
      </div>
      <div>
        <label className={labelCls}>
          Primeira parcela em
          <Required />
        </label>
        <DateInputBR className="input-field w-full" value={firstDate} onChange={setFirstDate} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>
          Descrição
          <Required />
        </label>
        <input className="input-field" maxLength={190} value={instDescription} onChange={(e) => setInstDescription(e.target.value)} placeholder="Ex.: Geladeira" />
        <p className="text-small mt-1">Cada parcela recebe o sufixo "i/N" (ex.: "Geladeira 1/10").</p>
      </div>
      <div>
        <label className={labelCls}>
          Valor total
          <Required />
        </label>
        <MoneyInput value={total} onChange={setTotal} />
      </div>
      <div>
        <label className={labelCls}>
          Nº de parcelas ({MIN_INSTALLMENTS}–{MAX_INSTALLMENTS})
          <Required />
        </label>
        <input
          type="number"
          className="input-field"
          min={MIN_INSTALLMENTS}
          max={MAX_INSTALLMENTS}
          value={count}
          onChange={(e) => setCount(Math.max(0, Math.trunc(Number(e.target.value) || 0)))}
        />
      </div>
      <div>
        <label className={labelCls}>Categoria</label>
        <CategorySelect categories={categories} movement={instMovement} value={instCategoryId} onChange={setInstCategoryId} />
      </div>
      <div>
        <label className={labelCls}>Observações</label>
        <input className="input-field" maxLength={1000} value={instNotes} onChange={(e) => setInstNotes(e.target.value)} />
      </div>
      {preview.length > 0 && (
        <div className="sm:col-span-2">
          <p className="text-label mb-1">Prévia das parcelas</p>
          <div className="rounded-lg border border-slate-200 max-h-48 overflow-y-auto custom-scrollbar">
            <table className="w-full text-sm">
              <tbody>
                {preview.map((p) => (
                  <tr key={p.index} className="border-t first:border-t-0 border-slate-100">
                    <td className="px-3 py-1.5 text-slate-500 w-16">
                      {p.index}/{preview.length}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{formatDateBR(p.date)}</td>
                    <td className={clsx('px-3 py-1.5 text-right tabular-nums', p.amount_cents < 0 ? 'text-red-600' : 'text-emerald-700')}>
                      {formatBRL(p.amount_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-small mt-1">A sobra de centavos fica na 1ª parcela; dia 31 cai no último dia dos meses mais curtos.</p>
        </div>
      )}
    </div>
  );

  const transferForm = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className={labelCls}>
          De (origem)
          <Required />
        </label>
        <select className="select-field" value={fromId} onChange={(e) => setFromId(e.target.value ? Number(e.target.value) : '')}>
          <AccountOptions accounts={accounts} />
        </select>
      </div>
      <div>
        <label className={labelCls}>
          Para (destino)
          <Required />
        </label>
        <select className="select-field" value={toId} onChange={(e) => setToId(e.target.value ? Number(e.target.value) : '')}>
          <option value="">Selecione…</option>
          {accounts
            .filter((a) => a.id !== fromId)
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>
          Data
          <Required />
        </label>
        <DateInputBR className="input-field w-full" value={trDate} onChange={setTrDate} />
      </div>
      <div>
        <label className={labelCls}>
          Valor
          <Required />
        </label>
        <MoneyInput value={trAmount} onChange={setTrAmount} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>Descrição</label>
        <input
          className="input-field"
          maxLength={200}
          value={trDescription}
          onChange={(e) => setTrDescription(e.target.value)}
          placeholder='Opcional — padrão "Transferência para <conta>" / "Transferência de <conta>"'
        />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>Observações</label>
        <input className="input-field" maxLength={1000} value={trNotes} onChange={(e) => setTrNotes(e.target.value)} />
      </div>
      <p className="sm:col-span-2 text-small">
        Cria duas pernas (saída na origem, entrada no destino) com a categoria de sistema "Transferência entre contas". Transferências
        não entram em receitas nem despesas, só no saldo.
      </p>
    </div>
  );

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Receipt size={18} className="text-slate-500" />
        <h2 className="text-h3">{title}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-4">
        {!isEdit && (
          <PillTabs
            tabs={[
              { id: 'single', label: 'Lançamento', icon: <Receipt size={15} /> },
              { id: 'installments', label: 'Parcelado', icon: <Layers size={15} /> },
              { id: 'transfer', label: 'Transferência', icon: <ArrowLeftRight size={15} /> },
            ]}
            active={mode}
            onChange={(id) => setMode(id as Mode)}
          />
        )}
        {isEdit || mode === 'single' ? singleForm : mode === 'installments' ? installmentsForm : transferForm}
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : mode === 'installments' ? `Criar ${count >= MIN_INSTALLMENTS ? count : ''} parcelas` : mode === 'transfer' ? 'Transferir' : 'Lançar'}
        </Button>
      </div>
    </Modal>
  );
};
